import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TableSortLabel from '@mui/material/TableSortLabel';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import { useData } from './DataProvider';

export default function FileInfo({ fileinfoUrl, buttonProps = {} }) {
  const [open, setOpen] = useState(false);
  const { files, setFileInfo } = useData();
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [orderBy, setOrderBy] = useState('FileName');
  const [order, setOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Any file with a hash has a VirusTotal page — a .pkg / .dmg / script / Mach-O has no PeFormat.
  const hasVirusTotalLink = (file) => !!file?.SHA256;

  // Helper function to highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <Box 
          key={index} 
          component="span" 
          sx={{ 
            backgroundColor: '#ffeb3b', 
            color: '#000',
            fontWeight: 'bold',
            padding: '0 2px',
            borderRadius: '2px'
          }}
        >
          {part}
        </Box>
      ) : part
    );
  };

  useEffect(() => {
    if (!fileinfoUrl) {
      setError('No fileinfo.json URL provided.');
      setFileInfo([]);
      setSelected(null);
      return;
    }

    const controller = new AbortController();
    fetch(fileinfoUrl, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load fileinfo.json from blob storage');
        return res.json();
      })
      .then(data => {
        const arr = Object.values(data);
        setFileInfo(arr);
        if (arr.length > 0) setSelected(arr[0]);
        setError(null);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') setError(e.message);
      });

    return () => controller.abort();
  }, [fileinfoUrl, setFileInfo]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrder('asc');
    }
  };

  const sortedFiles = React.useMemo(() => {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const normalizedSearch = searchTerm.toLowerCase();
    
    const filteredFiles = files.filter((file) => {
      if (!searchTerm) return true;
      return [file.FileName, file.SHA256]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
    
    return [...filteredFiles].sort((a, b) => {
      let aValue = a[orderBy] ?? '';
      let bValue = b[orderBy] ?? '';
      if (orderBy === 'FileSize') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return order === 'asc' ? collator.compare(aValue, bValue) : collator.compare(bValue, aValue);
    });
  }, [files, orderBy, order, searchTerm]);

  useEffect(() => {
    if (!selected) return;

    const stillVisible = sortedFiles.some(
      (file) => file.SHA256 === selected.SHA256 && file.FileName === selected.FileName
    );

    if (!stillVisible) {
      setSelected(sortedFiles[0] ?? null);
    }
  }, [sortedFiles, selected]);

  // Button to open dialog
  return (
    <React.Fragment>
      <Button
        size="small"
        startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen(true)}
        sx={{ py: 0.5, minHeight: 28 }}
        {...buttonProps}
      >
        File Info
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '90vh', maxWidth: 'none', maxHeight: 'none' } }}
      >
        <DialogContent sx={{ p: 3, height: 'calc(90vh - 70px)', boxSizing: 'border-box' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>File Info</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box p={0} display="flex" flexDirection="row" gap={4} height="calc(100% - 80px)">
            <Box flex={2} minHeight={0} display="flex" flexDirection="column">
              <Box mb={2} display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total files: {files.length} {searchTerm && `(${sortedFiles.length} matching)`}
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
              </Box>
              <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={orderBy === 'FileName' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'FileName'}
                          direction={orderBy === 'FileName' ? order : 'asc'}
                          onClick={() => handleSort('FileName')}
                        >
                          File Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={orderBy === 'FileSize' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'FileSize'}
                          direction={orderBy === 'FileSize' ? order : 'asc'}
                          onClick={() => handleSort('FileSize')}
                        >
                          Size
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={orderBy === 'SHA256' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'SHA256'}
                          direction={orderBy === 'SHA256' ? order : 'asc'}
                          onClick={() => handleSort('SHA256')}
                        >
                          SHA256 / VirusTotal
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedFiles.map((file, idx) => (
                      <TableRow
                        key={file.FileName || idx}
                        hover
                        selected={selected && selected.FileName === file.FileName}
                        onClick={() => setSelected(file)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>
                          {highlightText(file.FileName, searchTerm)}
                        </TableCell>
                        <TableCell align="right">{file.FileSize ? formatFileSize(Number(file.FileSize)) : ''}</TableCell>
                        <TableCell sx={{ maxWidth: 200, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {hasVirusTotalLink(file)
                            ? (
                              <Link
                                href={`https://www.virustotal.com/#/file/${file.SHA256}/detection`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {file.SHA256}
                              </Link>
                            ) : file.SHA256 || ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box flex={1} minWidth={320} minHeight={0} display="flex" flexDirection="column" height="100%">
              {selected ? (
                <Paper sx={{ p: 2, flex: 1, maxHeight: '100%', overflow: 'auto', minHeight: 0 }}>
                  {hasVirusTotalLink(selected) && (
                    <Box mb={1}>
                      <Link
                        href={`https://www.virustotal.com/#/file/${selected.SHA256}/detection`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: 'break-all', fontWeight: 500 }}
                      >
                        VirusTotal
                      </Link>
                    </Box>
                  )}
                  <List dense>
                    {Object.entries(selected).map(([key, value]) => (
                      <ListItem key={key} alignItems="flex-start" disablePadding>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{key}</Typography>}
                          secondary={Array.isArray(value)
                            ? value.length > 0
                              ? value.map((v, i) => <span key={i}>{v}<br /></span>)
                              : <em>None</em>
                            : (typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value))}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : (
                <Typography color="text.secondary">Select a file to see details</Typography>
              )}
            </Box>
          </Box>
          {error && <Typography color="error" sx={{ p: 2 }}>{error}</Typography>}
          {!files.length && !error && <Typography sx={{ p: 2 }}>Loading file info...</Typography>}
        </DialogContent>
        <Divider sx={{ width: '100%' }} />
        <Box sx={{ mt: 2, mb: 3, px: 3, textAlign: 'right' }}>
          <Button onClick={() => setOpen(false)} variant="contained">Close</Button>
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
